import type { Action } from './action'
import type { Cell } from './matrix'
import type { Posture } from './posture'

export type ExpandScreenInput = {
  roles: Record<string, 'allow' | 'deny'>
  states: Posture[]
  actions: Action[]
  /** Optional overrides keyed by `${role}::${state}` */
  overrides?: Record<
    string,
    Partial<Pick<Cell, 'visibility' | 'actions_present' | 'actions_absent'>>
  >
}

/**
 * Expand default cells from roles × states × actions.
 * Deny roles → visibility deny and empty actions_present.
 * Allow roles → screen actions whose roles filter includes the role (or no filter).
 * Denied posture always yields visibility deny and omits product actions.
 */
export function expandDefaultCells(input: ExpandScreenInput): Cell[] {
  const states = input.states.length > 0 ? input.states : (['ready'] as Posture[])
  const cells: Cell[] = []

  for (const [role, roleVisibility] of Object.entries(input.roles)) {
    for (const state of states) {
      const key = `${role}::${state}`
      const override = input.overrides?.[key]
      const forceDeny = roleVisibility === 'deny' || state === 'denied'

      if (forceDeny) {
        cells.push({
          role,
          state,
          visibility: 'deny',
          actions_present: [],
          actions_absent: override?.actions_absent ?? input.actions.map((a) => a.id),
        })
        continue
      }

      const present =
        override?.actions_present ??
        input.actions.filter((a) => !a.roles || a.roles.includes(role)).map((a) => a.id)

      cells.push({
        role,
        state,
        visibility: override?.visibility ?? 'allow',
        actions_present: present,
        ...(override?.actions_absent ? { actions_absent: override.actions_absent } : {}),
      })
    }
  }

  return cells
}

/** Merge explicit cells over expanded defaults (explicit wins by role+state). */
export function mergeCellOverrides(defaults: Cell[], overrides: Cell[]): Cell[] {
  const map = new Map<string, Cell>()
  for (const c of defaults) map.set(`${c.role}::${c.state}`, c)
  for (const c of overrides) map.set(`${c.role}::${c.state}`, c)
  return [...map.values()]
}

export type ScreenLike = {
  roles: Record<string, 'allow' | 'deny'>
  states: Posture[]
  actions: Action[]
  cells: Cell[]
}

/** Ensure a screen-like object has cells; expand when empty. */
export function ensureCells<T extends ScreenLike>(screen: T): T {
  if (screen.cells.length > 0) return screen
  return {
    ...screen,
    cells: expandDefaultCells({
      roles: screen.roles,
      states: screen.states,
      actions: screen.actions,
    }),
  }
}
